import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Settings, Save, Plus, Trash2 } from "lucide-react";

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/settings");
      setSettings(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { id, created_at, updated_at, _id, ...payload } = settings;
      await api.patch("/settings", payload);
      toast.success("Pengaturan tersimpan");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (loading || !settings) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;

  const setField = (k, v) => setSettings(s => ({ ...s, [k]: v }));
  const setJam = (idx, k, v) => setSettings(s => ({ ...s, jam_default: s.jam_default.map((j, i) => i === idx ? { ...j, [k]: v } : j) }));
  const addJam = () => setSettings(s => ({ ...s, jam_default: [...(s.jam_default || []), { sesi: (s.jam_default?.length || 0) + 1, jam_mulai: "15:30", jam_selesai: "16:30" }] }));
  const removeJam = (idx) => setSettings(s => ({ ...s, jam_default: s.jam_default.filter((_, i) => i !== idx) }));

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><Settings className="h-6 w-6 text-emerald-600" /> Pengaturan Sistem</h1>
          <p className="text-sm text-slate-500">Konfigurasi umum madrasah.</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Simpan</>}
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Identitas Madrasah</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label>Nama Madrasah</Label><Input value={settings.nama_madrasah || ""} onChange={(e) => setField("nama_madrasah", e.target.value)} /></div>
          <div><Label>Kepala Madrasah</Label><Input value={settings.kepala_madrasah || ""} onChange={(e) => setField("kepala_madrasah", e.target.value)} /></div>
          <div><Label>Tahun Berdiri</Label><Input value={settings.tahun_berdiri || ""} onChange={(e) => setField("tahun_berdiri", e.target.value)} /></div>
          <div><Label>Telepon</Label><Input value={settings.telepon || ""} onChange={(e) => setField("telepon", e.target.value)} /></div>
          <div className="md:col-span-2"><Label>Alamat</Label><Input value={settings.alamat || ""} onChange={(e) => setField("alamat", e.target.value)} /></div>
          <div className="md:col-span-2"><Label>Logo URL</Label><Input value={settings.logo_url || ""} onChange={(e) => setField("logo_url", e.target.value)} placeholder="https://..." /></div>
          <div className="md:col-span-2"><Label>Catatan Kaki (opsional)</Label><Input value={settings.footer_note || ""} onChange={(e) => setField("footer_note", e.target.value)} /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Mode Absensi</CardTitle></CardHeader>
        <CardContent>
          <div className="max-w-md">
            <Label>Mode Default</Label>
            <Select value={settings.absensi_mode || "hari"} onValueChange={(v) => setField("absensi_mode", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="hari">Per Hari (1 absen/santri/hari)</SelectItem>
                <SelectItem value="sesi">Per Sesi (1 absen/santri/sesi — flow ustadz)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-1">Mode “hari” cocok untuk absen umum. Mode “sesi” mewajibkan ustadz absen setelah “Mulai Absensi” dari jadwal.</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center justify-between">Jam Default Sesi <Button size="sm" variant="outline" onClick={addJam}><Plus className="h-3 w-3 mr-1" /> Tambah Sesi</Button></CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(settings.jam_default || []).map((j, i) => (
            <div key={i} className="grid grid-cols-4 gap-2 items-end">
              <div><Label>Sesi</Label><Input type="number" value={j.sesi} onChange={(e) => setJam(i, "sesi", Number(e.target.value))} /></div>
              <div><Label>Mulai</Label><Input type="time" value={j.jam_mulai} onChange={(e) => setJam(i, "jam_mulai", e.target.value)} /></div>
              <div><Label>Selesai</Label><Input type="time" value={j.jam_selesai} onChange={(e) => setJam(i, "jam_selesai", e.target.value)} /></div>
              <Button size="icon" variant="ghost" onClick={() => removeJam(i)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Hari Libur Kustom</CardTitle></CardHeader>
        <CardContent>
          <p className="text-xs text-slate-500 mb-2">Selasa & Kamis sudah otomatis libur. Tambahkan tanggal khusus (satu per baris, format YYYY-MM-DD).</p>
          <textarea className="w-full min-h-[80px] rounded-md border border-slate-200 p-2 text-sm font-mono"
                    value={(settings.hari_libur_custom || []).join("\n")}
                    onChange={(e) => setField("hari_libur_custom", e.target.value.split("\n").map(x => x.trim()).filter(Boolean))} />
        </CardContent>
      </Card>
    </div>
  );
}
